import React, { useEffect, useRef, useState } from "react";
import { Skeleton, Typography } from "antd";
import { useTranslation } from "react-i18next";
import TranslateText from "../../resuableComponents/TranslateText";
import { useDispatch, useSelector } from "react-redux";
import { formatPaymentDictionary, generateDynamicLogo, getComparator, sortFunction } from "../../../Utils/utils";
import MobilePaymentNavigation from "./mobilePaymentNavigation/MobilePaymentNavigation";
import {
  removeSelectedIntruments,
  selectSavedInstruments,
} from "../../../slices/SavedInstrumentSlice";
import "./paymentOptionsContainer.css";
import { selectBaseColor } from "../../../slices/PaymentSessionDetailsSlice";
import BottomSheet from "../../resuableComponents/BottomSheet/BottomSheet";
import PaymentIntrumentSelection from "./paymentInstrumentSelection/PaymentIntrumentSelection";
import {
  changeActivePaymentMethod,
  changePaymentMethodInfo,
  selectActivePaymentMethod,
} from "../../../slices/PaymentButtonSlice";
import Emi from "./emi/emi";
import { resetCardFormDetails } from "../../../slices/CardSlice";
import { resetCardBinData } from "../../../slices/BinSlice";
import { TOP_BANKS, UPI_INTENT_APPS } from "../../../staticData";

const PaymentOptionsMobileContainer = ({ tabList, darkColor, lightColor }) => {
  const { t } = useTranslation();
  const paymentMethodDictionary = useSelector((state) => state?.paymentSessionDetails?.paymentMethodDictionary);
  let savedInstruments = useSelector((state) => selectSavedInstruments(state));
  const baseColor = useSelector((state) => selectBaseColor(state));
  const [openUpiBottomSheet, setOpenUpiBottomSheet] = useState(false);
  const [upiCollectInstruments, setUpiCollectInstruments] = useState([]);
  const [cardPaymentMethod, setCardPaymentMethod] = useState([]);
  const dispatch = useDispatch();
  const [isPaymentLoading, setIsPaymentLoading] = useState(true);
  const paymentMethodsLoading = useSelector((state) => state.paymentSessionDetails.paymentMethodsLoading);
  const sessionDetailsLoading = useSelector((state) => state.paymentSessionDetails.sessionDetailsLoading);
  const [bottomSheetPaymentOption, setBottomSheetPaymentOption] = useState([]);
  const [savedPaymentTitle, setSavedPaymentTitle] = useState('SAVED_UPI');
  const selectedActivePaymentTab = useSelector(state => state?.offers?.selectedActivePaymentTab)
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const paymentMethodDictionaryFormatted = formatPaymentDictionary(paymentMethodDictionary)

  const getTabPreviewIcons = (type) => {
    if (type === "Upi" || type === "UpiOneTimeMandate") {
      return UPI_INTENT_APPS;
    }
    const entries = paymentMethodDictionary?.[type];
    if (!entries?.length) return [];
    switch (type) {
      case "NetBanking": {
        const priorityMatches = TOP_BANKS
          .map((bankName) => entries.find((entry) => entry?.title === bankName))
          .filter(Boolean);
        const remaining = entries.filter((entry) => !priorityMatches.includes(entry));
        return [...priorityMatches, ...remaining].slice(0, 3);
      }
      case "Card":
        return entries.slice(0, 3);
      case "Wallet":
      case "BuyNowPayLater":
        return entries.length > 1 ? entries.slice(0, 3) : [];
      case "Emi": {
        const getIssuerTitle = (entry) =>
          entry?.emiMethod?.issuerTitle || entry?.emiMethod?.cardlessEmiProviderTitle;
        const priorityMatches = TOP_BANKS
          .map((bankName) => entries.find((entry) => getIssuerTitle(entry) === bankName))
          .filter(Boolean);
        const remaining = entries.filter((entry) => !priorityMatches.includes(entry));
        return [...priorityMatches, ...remaining].slice(0, 3);
      }
      default:
        return [];
    }
  };

  useEffect(() => {
    if (Object.keys(paymentMethodDictionaryFormatted)?.length >= 0) {
      const timer = setTimeout(() => {
        setIsPaymentLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [paymentMethodDictionaryFormatted]);
  useEffect(() => {
    if (savedInstruments) {
      let upiCollect = savedInstruments?.filter(
        (instrumentInfo) => instrumentInfo?.brand === "UpiCollect"
      );
      setUpiCollectInstruments(upiCollect);
      let card = savedInstruments?.filter(
        (cardInfo) => cardInfo?.type === "Card"
      );
      setCardPaymentMethod(card);
    }
  }, [savedInstruments]);

  const handleClose = () => {
    dispatch(removeSelectedIntruments());
    setOpenUpiBottomSheet(false);
  };

  const handleSavedClick = (paymentOptionType) => {
    if (paymentOptionType === 'UPI') {
      let upiCollect = paymentMethodDictionaryFormatted["Upi"].find(
        (upiInfo) => upiInfo.brand === "UpiCollect"
      );
      dispatch(changeActivePaymentMethod("UpiSaved"));
      setSavedPaymentTitle('SAVED_UPI')
      dispatch(changePaymentMethodInfo(upiCollect));
      setBottomSheetPaymentOption(upiCollectInstruments);
      setOpenUpiBottomSheet(true);
    } else if (paymentOptionType === 'Card') {
      setBottomSheetPaymentOption(cardPaymentMethod);
      setSavedPaymentTitle('SAVED_CARDS')
      setOpenUpiBottomSheet(true);
    }
  }

  useEffect(() => {
    const configMethods = sessionDetails?.configs?.paymentMethods || [];
    const priorityOrder = ["Paynow", "Upi", "UpiOneTimeMandate", "Card"];
    let defaultMethod = priorityOrder.find((type) =>
      configMethods.some((m) => m?.type === type)
    );
    if (!defaultMethod) {
      defaultMethod = tabList?.find((tab) =>
        configMethods.some((m) => m?.type === tab?.type)
      )?.type;
    }
    dispatch(changeActivePaymentMethod(tabList[selectedActivePaymentTab]?.type || defaultMethod));
  }, [sessionDetails]);

  const activePaymentMethod = useSelector(selectActivePaymentMethod);
  const prevActivePaymentMethodRef = useRef(activePaymentMethod);
  useEffect(() => {
    if (prevActivePaymentMethodRef.current === "Card" && activePaymentMethod !== "Card") {
      dispatch(resetCardFormDetails());
      dispatch(resetCardBinData());
    }
    prevActivePaymentMethodRef.current = activePaymentMethod;
  }, [activePaymentMethod]);

  const savedInstrumentsOption = (type) => {
    if (type === "Upi" && upiCollectInstruments?.length > 0) {
      return (
        <div
          className="domestic-saved-instruments dynamic-font-family"
          onClick={() => {
            handleSavedClick('UPI')
          }}
        >
          <Typography className="domestic-saved-instruments-title dynamic-title">
            <TranslateText label={"SAVED_UPI"}>Saved UPI</TranslateText>
          </Typography>
          {generateDynamicLogo("rightArraow", "14px", "14px", baseColor)}
        </div>
      );
    }
    // Card "View all" lives under the saved cards (see CardPaymentOption).
    return null;
  };

  const shouldShowAllPaymentOptions = [
    "Wallet",
    "NetBanking",
    "BuyNowPayLater",
    "Emi"
  ].some((method) => paymentMethodDictionaryFormatted.hasOwnProperty(method));

  const isOnlyUpiQr =
    paymentMethodDictionaryFormatted.Upi?.length === 1 &&
    paymentMethodDictionaryFormatted.Upi[0].brand === "UpiQr";

  const isOnlyUpiQrOtm =
    paymentMethodDictionaryFormatted.Upi?.length === 1 &&
    paymentMethodDictionaryFormatted.Upi[0].brand === "UpiQrOtm";


  return (
    <>
      {isPaymentLoading || paymentMethodDictionaryFormatted === undefined || sessionDetailsLoading ? (
        // {isPaymentLoading || paymentMethodsLoading ? (
        <Skeleton active size="large" />
      ) : Object.keys(paymentMethodDictionaryFormatted)?.length ? (
        <>
          <BottomSheet
            title={t(savedPaymentTitle)}
            type={"pay"}
            open={openUpiBottomSheet}
            handleClose={handleClose}
            children={
              <PaymentIntrumentSelection data={bottomSheetPaymentOption} />
            }
          />
          {/* {upiCollectInstruments?.length > 0 && (
            <BottomSheet
              title={t("SAVED_UPI")}
              type={"pay"}
              open={openUpiBottomSheet}
              handleClose={handleClose}
              children={
                <PaymentIntrumentSelection data={bottomSheetPaymentOption} />
              }
            />
          )}
          {cardPaymentMethod?.length > 0 && (
            <BottomSheet
              title={t("SAVED_CARDS")}
              open={openUpiBottomSheet}
              handleClose={handleClose}
              children={<PaymentIntrumentSelection data={bottomSheetPaymentOption} />}
            />
          )} */}
          {tabList?.map((tab, id) => {
            const shouldRenderTab =
              tab?.type === "Upi" && isOnlyUpiQr
                ? false
                : (tab?.type === "Upi" ||
                  tab?.type === "UpiOneTimeMandate" ||
                  tab?.type === "Card" ||
                  tab?.type === "Recommended" ||
                  tab?.type === "Paynow") &&
                tab?.type in paymentMethodDictionaryFormatted;
            if (!shouldRenderTab) return null;

            const headerTitle =
              tab?.type === "Recommended"
                ? "Recommended"
                : paymentMethodDictionaryFormatted[tab?.type]?.length > 1
                  ? paymentMethodDictionaryFormatted[tab?.type][0]?.typeTitle
                  : paymentMethodDictionaryFormatted[tab?.type][0]?.title;

            return (
              <MobilePaymentNavigation
                key={id}
                icon={tab?.logo}
                tabType={tab.type.toUpperCase()}
                title={headerTitle}
                children={tab?.type === "Card" ? tab?.content : (tab?.mobileContent || tab?.content)}
                collapseKey={tab?.type}
                whiteBackground
                activeColor={darkColor}
                destroyOnCollapse
                extra={savedInstrumentsOption(tab?.type)}
                previewIcons={getTabPreviewIcons(tab?.type)}
                smallPreviewIcons={tab?.type === "Upi" || tab?.type === "UpiOneTimeMandate"}
              />
            );
          })}

          {shouldShowAllPaymentOptions &&
            tabList?.map((tab, id) => {
              if (tab?.type === "Emi" && tab?.type in paymentMethodDictionaryFormatted) {
                return (
                  <MobilePaymentNavigation
                    key={id}
                    icon={tab?.logo}
                    tabType={tab.type.toUpperCase()}
                    title={
                      paymentMethodDictionaryFormatted[tab?.type]?.length > 1
                        ? paymentMethodDictionaryFormatted[tab?.type][0]?.typeTitle
                        : paymentMethodDictionaryFormatted[tab?.type][0]?.title
                    }
                    children={
                      <Emi
                        key={id}
                        emiMethodInfo={
                          paymentMethodDictionaryFormatted?.[tab?.type]
                        }
                      />
                    }
                    collapseKey={tab?.type}
                    whiteBackground
                    activeColor={darkColor}
                    destroyOnCollapse
                    previewIcons={getTabPreviewIcons(tab?.type)}
                  />
                );
              } else if (
                tab?.type !== "Upi" &&
                tab?.type !== "Card" &&
                tab?.type !== "UpiOneTimeMandate" &&
                tab?.type !== "Recommended" &&
                tab?.type !== "Paynow" &&
                tab?.type in paymentMethodDictionaryFormatted
              ) {
                const isSingleInstrument = paymentMethodDictionaryFormatted[tab?.type]?.length === 1;
                return (
                  <MobilePaymentNavigation
                    key={id}
                    icon={tab?.logo}
                    tabType={isSingleInstrument ? undefined : tab.type.toUpperCase()}
                    title={
                      isSingleInstrument
                        ? paymentMethodDictionaryFormatted[tab?.type][0]?.title
                        : paymentMethodDictionaryFormatted[tab?.type][0]?.typeTitle
                    }
                    children={
                      <PaymentIntrumentSelection
                        data={
                          tab?.type === "Wallet" ||
                            tab?.type === "NetBanking" ||
                            tab?.type === "BuyNowPayLater"
                            ? sortFunction(
                              paymentMethodDictionaryFormatted[tab?.type],
                              getComparator("asc", "title")
                            )
                            : paymentMethodDictionaryFormatted[tab?.type]
                        }
                        searchComponent={
                          tab?.type === "NetBanking" ? true : false
                        }
                        searchPlaceholder={
                          tab?.type === "NetBanking"
                            ? `${t("SEARCH_BANKS")}`
                            : `${t("SEARCH")}`
                        }
                      />
                    }
                    collapseKey={tab?.type}
                    whiteBackground
                    activeColor={darkColor}
                    destroyOnCollapse
                    previewIcons={getTabPreviewIcons(tab?.type)}
                  />
                );
              } else {
                return null;
              }
            })}
        </>
      ) : (
        <div className="domestic-empty-po-root">
          <div className="no-payment-options-div">
            <img
              src="/assets/paymentOptions/paymentoptions.svg"
              alt="payment"
            />
            <Typography className="no-payment-options-Heading dynamic-font-family">
              <TranslateText label={"NO_PAYMENT_OPTION_AVAILABLE"}>No payment option available</TranslateText>
            </Typography>
            <Typography className="no-payment-options-subHeading dynamic-font-family">
              <TranslateText label={"CHANGE_COUNTRY_FOR_PAYMENT_OPTIONS"}>Please change your country in the address to see the available payment options</TranslateText>
            </Typography>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentOptionsMobileContainer;
